/**
 * Tooltip 配置模块。
 * 仅负责管理 tooltip 组件的默认配置、样式开关等 ECharts 配置相关逻辑。
 * 后续若需扩展 tooltip 样式或轴指示器默认值，请在此文件维护。
 */
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
import {
  ChartOption,
  IChartConfig,
  IPlugConfig,
} from '@customComp/Chart/_config/_interface';
import type { TooltipComponentOption } from 'echarts/components';
import { TOOLTIP_WRAP_CLASS_NAME } from './constants';

type TTooltipAxisPointer = NonNullable<TooltipComponentOption['axisPointer']>;

export const getTooltipConfig = (
  config: { tooltipMinTopPx?: number; } & Partial<TooltipComponentOption> = {},
): TooltipComponentOption => {
  const { axisPointer, className, tooltipMinTopPx, ...rest } = config;
  const defaultAxisPointer: TTooltipAxisPointer = {
    type: 'shadow',
    z: 10,
    label: {
      show: true,
      backgroundColor: '#ffffff',
      fontSize: 10,
      color: '#262832',
      fontWeight: 700,
    },
    lineStyle: {
      color: '#cccccc',
      width: 1,
      type: 'solid',
    },
  };
  return {
    className: classNames(TOOLTIP_WRAP_CLASS_NAME, className),
    appendToBody: true,
    trigger: 'axis',
    // 是否将tooltip框限制在图表的区域内
    // confine: true,
    enterable: true,
    position: (point: [number, number], params: any, dom: any, rect: any, size: any) => {
      const gap = 20;
      const topGap = 20;
      const [mouseX = 0, mouseY = 0] = point || [];
      const [tooltipWidth = 0, tooltipHeight = 0] = size?.contentSize || [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [viewWidth = 0, viewHeight = 0] = size?.viewSize || [];
      const isLeftArea = mouseX <= viewWidth / 2;
      // 鼠标在左半区时浮窗放右侧，右半区时放左侧
      let left = isLeftArea ? mouseX + gap : mouseX - tooltipWidth - gap;
      let top = mouseY - tooltipHeight - topGap;
      // 边界保护，避免浮窗超出图表可视区
      left = Math.max(0, Math.min(left, viewWidth - tooltipWidth));
      // 传入 tooltipMinTopPx（报表贴顶卡片通常为 DashboardLayout/_config 的 TOOLTIP_MIN_TOP_PX 再扣筛选区高度）时：允许浮窗略向上超出视区并做上下界保护
      if (typeof tooltipMinTopPx === 'number') {
        top = Math.max(
          tooltipMinTopPx,
          Math.min(top, viewHeight - tooltipHeight),
        );
      }
      return [left, top];
    },
    axisPointer: {
      ...defaultAxisPointer,
      ...axisPointer,
      label: {
        ...defaultAxisPointer.label,
        ...axisPointer?.label,
      },
      lineStyle: {
        ...defaultAxisPointer.lineStyle,
        ...axisPointer?.lineStyle,
      },
    },
    padding: 0,
    borderWidth: 0,
    ...rest,
  };
};

export const setToolTipSetting = (option: ChartOption, chartConfig?: IChartConfig): ChartOption => {
  // 如果 tooltip 不存在，直接返回，避免后续操作出错
  if (!option?.tooltip) {
    return option;
  }
  try {
    const { showTotal = true, showShare = true } = chartConfig?.tooltipSetting || {};
    const tooltipConfig = option?.tooltip;
    if (tooltipConfig && !Array.isArray(tooltipConfig) && typeof tooltipConfig === 'object') {
      tooltipConfig.className = classNames(
        tooltipConfig?.className,
        { 'dbl-echarts-tooltip-total-hide': !showTotal },
        { 'dbl-echarts-tooltip-share-hide': !showShare },
      );
    }
  } catch (error) {
    console.warn('[setToolTipSetting] Error setting tooltip:', error);
  }
  return option;
};

/**
 * 设置 tooltip 最大尺寸
 * @param option ECharts 配置项对象
 * @param _chartConfig 图表配置（预留参数）
 * @param config 配置项
 * @returns 更新后的配置
 */
export const setTooltipMaxSize = (
  option: ChartOption,
  _chartConfig?: IChartConfig,
  config?: IPlugConfig,
): ChartOption => {
  const newOption = cloneDeep(option);
  const { width = 400, height } = config?.chartLayoutRect || {};
  const tooltipConfig = newOption?.tooltip;
  const minWidth = width < 400 ? 400 : width;
  if (
    width
    && height
    && tooltipConfig
    && !Array.isArray(tooltipConfig)
    && typeof tooltipConfig === 'object'
  ) {
    newOption.tooltip = {
      ...tooltipConfig,
      extraCssText: `${tooltipConfig?.extraCssText || ''}; --chart-max-width: ${minWidth}px; --chart-max-height: ${height}px;`,
    };
  }
  return newOption;
};


